import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../../components/LoadingSpinner'
import GroupLeaderBottomNav from '../../../components/GroupLeaderBottomNav'
import { 
  ArrowLeft,
  CalendarBlank,
  MapPin,
  CaretDown,
  User
} from '@phosphor-icons/react'

export default function GroupLeaderEventDetails() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [event, setEvent] = useState(null)
  const [eventStatus, setEventStatus] = useState(null)
  const [eventCategory, setEventCategory] = useState(null)
  const [loading, setLoading] = useState(true)
  
  // Members Attendance state
  const [subgroups, setSubgroups] = useState([])
  const [members, setMembers] = useState([])
  const [selectedDepartment, setSelectedDepartment] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [loadingMembers, setLoadingMembers] = useState(false)

  useEffect(() => {
    loadEventDetails()
  }, [id])

  useEffect(() => {
    if (eventCategory) {
      loadMembersAttendance()
    }
  }, [eventCategory])

  const loadEventDetails = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()

      const { data: eventData } = await supabase
        .from('events')
        .select('*')
        .eq('id', id)
        .single()

      if (!eventData) {
        navigate('/group-leader/events')
        return
      }

      setEvent(eventData)

      // Determine event category
      const now = new Date()
      const todayDate = now.toISOString().split('T')[0]
      const currentTime = now.toTimeString().slice(0, 5)
      const eventDate = eventData.start_date
      const startTime = eventData.start_time
      const endTime = eventData.end_time

      let category = 'past'
      
      if (eventDate > todayDate) {
        category = 'upcoming'
      } else if (eventDate === todayDate) {
        if (startTime > currentTime) {
          category = 'upcoming'
        } else if (endTime > currentTime) {
          category = 'active'
        } else {
          category = 'past'
        }
      }

      setEventCategory(category)

      // Check attendance
      const { data: attendance } = await supabase
        .from('attendance_records')
        .select('*')
        .eq('event_id', id)
        .eq('user_id', user.id)
        .maybeSingle()

      if (attendance) {
        setEventStatus('attended')
      } else {
        const { data: confirmation } = await supabase
          .from('event_confirmations')
          .select('status')
          .eq('event_id', id)
          .eq('user_id', user.id)
          .maybeSingle()

        if (category === 'past') {
          // For past events: if not attended, it's either missed or declined
          if (confirmation?.status === 'declined') {
            setEventStatus('declined')
          } else {
            setEventStatus('missed')
          }
        } else {
          // For upcoming/active events: use confirmation status
          if (confirmation?.status === 'confirmed') {
            setEventStatus('confirmed')
          } else if (confirmation?.status === 'declined') {
            setEventStatus('declined')
          } else {
            setEventStatus('pending')
          }
        }
      }

    } catch (error) {
      console.error('Load event details error:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadMembersAttendance = async () => {
    try {
      setLoadingMembers(true)
      const { data: { user } } = await supabase.auth.getUser()

      // Get Group Leader's group (depth 2)
      const { data: groupMembership } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', user.id)
        .single()

      if (!groupMembership) {
        setLoadingMembers(false)
        return
      }

      const { data: leaderGroup } = await supabase
        .from('groups')
        .select('id, name, depth')
        .eq('id', groupMembership.group_id)
        .eq('depth', 2)
        .single()

      if (!leaderGroup) {
        setLoadingMembers(false)
        return
      }

      // Get all subgroups under this group (depth 3)
      const { data: subgroupsData } = await supabase
        .from('groups')
        .select('id, name')
        .eq('parent_id', leaderGroup.id)
        .eq('depth', 3)
        .order('name')

      setSubgroups(subgroupsData || [])

      // Get all members in these subgroups
      const subgroupIds = (subgroupsData || []).map(sg => sg.id)

      if (subgroupIds.length === 0) {
        setMembers([])
        setLoadingMembers(false)
        return
      }

      const { data: groupMembersData } = await supabase
        .from('group_members')
        .select(`
          user_id,
          group_id,
          users:user_id (
            id,
            name,
            email,
            profile_photo_url
          )
        `)
        .in('group_id', subgroupIds)

      // For each member, check their status for this event
      const membersWithStatus = await Promise.all(
        (groupMembersData || []).map(async (gm) => {
          const memberId = gm.user_id
          
          // Find subgroup name
          const subgroup = subgroupsData.find(sg => sg.id === gm.group_id)

          // Check attendance
          const { data: attendance } = await supabase
            .from('attendance_records')
            .select('id')
            .eq('event_id', id)
            .eq('user_id', memberId)
            .maybeSingle()

          let status = 'pending'

          if (attendance) {
            status = 'attended'
          } else {
            const { data: confirmation } = await supabase
              .from('event_confirmations')
              .select('status')
              .eq('event_id', id)
              .eq('user_id', memberId)
              .maybeSingle()

            if (eventCategory === 'past') {
              if (confirmation?.status === 'declined') {
                status = 'declined'
              } else {
                status = 'missed'
              }
            } else {
              if (confirmation?.status === 'confirmed') {
                status = 'confirmed'
              } else if (confirmation?.status === 'declined') {
                status = 'declined'
              } else {
                status = 'pending'
              }
            }
          }

          return {
            ...gm.users,
            subgroup: subgroup?.name || 'Unknown',
            subgroup_id: gm.group_id,
            status
          }
        })
      )

      // Sort by name
      membersWithStatus.sort((a, b) => (a.name || '').localeCompare(b.name || ''))

      setMembers(membersWithStatus)
    } catch (error) {
      console.error('Load members attendance error:', error)
    } finally {
      setLoadingMembers(false)
    }
  }

  const handleConfirm = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      await supabase
        .from('event_confirmations')
        .upsert({
          event_id: id,
          user_id: user.id,
          status: 'confirmed',
          confirmed_at: new Date().toISOString()
        }, {
          onConflict: 'event_id,user_id'
        })

      setEventStatus('confirmed')
    } catch (error) {
      console.error('Confirm event error:', error)
    }
  }

  const handleDecline = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      await supabase
        .from('event_confirmations')
        .upsert({
          event_id: id,
          user_id: user.id,
          status: 'declined',
          confirmed_at: new Date().toISOString()
        }, {
          onConflict: 'event_id,user_id'
        })

      setEventStatus('declined')
    } catch (error) {
      console.error('Decline event error:', error)
    }
  }

  const getStatusBadge = () => {
    if (eventStatus === 'attended') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-[#EAF5ED] text-[#2B9646] outline outline-1 outline-[#BFE0C8]">
          Attended
        </span>
      )
    }
    if (eventStatus === 'confirmed') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-[#EAF5ED] text-[#2B9646] outline outline-1 outline-[#BFE0C8]">
          Confirmed
        </span>
      )
    }
    if (eventStatus === 'declined') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-[#FEF0EB] text-[#A94C25] outline outline-1 outline-[#FBD3C2]">
          Declined
        </span>
      )
    }
    if (eventStatus === 'missed') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-[#FEF0EB] text-[#A94C25] outline outline-1 outline-[#FBD3C2]">
          Missed
        </span>
      )
    }
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-slate-50 text-slate-500 outline outline-1 outline-[#E2E8F0]">
        Pending
      </span>
    )
  }

  const getFilteredMembers = () => {
    let filtered = members

    // Filter by department
    if (selectedDepartment !== 'all') {
      filtered = filtered.filter(m => m.subgroup_id === selectedDepartment)
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(m => m.status === selectedStatus)
    }

    return filtered
  }

  const getStats = () => {
    // Filter by department only, not status
    let membersForStats = members

    if (selectedDepartment !== 'all') {
      membersForStats = members.filter(m => m.subgroup_id === selectedDepartment)
    }

    if (eventCategory === 'past') {
      return {
        attended: membersForStats.filter(m => m.status === 'attended').length,
        missed: membersForStats.filter(m => m.status === 'missed').length,
        declined: membersForStats.filter(m => m.status === 'declined').length
      }
    } else {
      return {
        confirmed: membersForStats.filter(m => m.status === 'confirmed').length,
        declined: membersForStats.filter(m => m.status === 'declined').length,
        pending: membersForStats.filter(m => m.status === 'pending').length
      }
    }
  }

  const getMemberStatusBadge = (status) => {
    if (status === 'attended') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-[#EAF5ED] text-[#2B9646] outline outline-1 outline-[#BFE0C8]">
          Attended
        </span>
      )
    }
    if (status === 'confirmed') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-[#EAF5ED] text-[#2B9646] outline outline-1 outline-[#BFE0C8]">
          Confirmed
        </span>
      )
    }
    if (status === 'declined') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-[#FEF0EB] text-[#A94C25] outline outline-1 outline-[#FBD3C2]">
          Declined
        </span>
      )
    }
    if (status === 'missed') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-[#FEF0EB] text-[#A94C25] outline outline-1 outline-[#FBD3C2]">
          Missed
        </span>
      )
    }
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-slate-50 text-slate-500 outline outline-1 outline-[#E2E8F0]">
        Pending
      </span>
    )
  }

  const getInitials = (name) => {
    if (!name) return '?'
    const parts = name.trim().split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const stringToHash = (str) => {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash)
    }
    return Math.abs(hash)
  }

  const getAvatarColor = (name) => {
    const colors = [
      { bg: 'bg-blue-100', text: 'text-blue-700' },
      { bg: 'bg-purple-100', text: 'text-purple-700' },
      { bg: 'bg-green-100', text: 'text-green-700' },
      { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      { bg: 'bg-red-100', text: 'text-red-700' },
      { bg: 'bg-pink-100', text: 'text-pink-700' },
      { bg: 'bg-indigo-100', text: 'text-indigo-700' },
      { bg: 'bg-teal-100', text: 'text-teal-700' }
    ]
    const hash = stringToHash(name || 'User')
    return colors[hash % colors.length]
  }

  if (loading) {
    return <LoadingSpinner message="Loading event details..." />
  }

  if (!event) {
    return null
  }

  const isVirtual = event.event_type === 'virtual'

  return (
    <>
      <div className="bg-white min-h-screen pb-20">
        {/* Header */}
        <div className="bg-white px-6 pt-10 pb-4">
          <div className="flex items-center relative">
            <button
              onClick={() => navigate(-1)}
              className="bg-slate-50 border border-slate-200 rounded-[20px] p-2"
            >
              <ArrowLeft size={20} weight="regular" className="text-slate-600" />
            </button>
            <h1 className="text-xl font-semibold text-slate-800 absolute left-1/2 transform -translate-x-1/2">
              Event details
            </h1>
          </div>
        </div>

        {/* Content */}
        <div className="px-6">
          {/* Banner Image */}
          {event.banner_url ? (
            <img 
              src={event.banner_url} 
              alt={event.title}
              className="w-full h-[367px] object-cover rounded-[20px] mb-4"
            />
          ) : (
            <div className="w-full h-[367px] bg-gradient-to-br from-purple-500 via-purple-600 to-orange-400 rounded-[20px] mb-4 flex items-center justify-center">
              <CalendarBlank size={64} weight="light" className="text-white opacity-50" />
            </div>
          )}

          {/* Title */}
          <h2 className="text-3xl font-semibold text-[#1E293B] mb-1 leading-tight">{event.title}</h2>
          
          {/* Date and Time - Single line */}
          <p className="text-base text-[#475569] mb-3 font-normal">
            {new Date(event.start_date).toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric' 
            })} · {event.start_time}
          </p>

          {/* Status and Type badges */}
          <div className="flex items-center gap-2 mb-6">
            {getStatusBadge()}
            <span className="px-2.5 py-1 rounded-full text-sm font-normal bg-[#EAF0FF] text-[#0F1F4C]">
              {isVirtual ? 'Virtual event' : 'Physical event'}
            </span>
          </div>

          {/* Venue / Link Section */}
          <div className="mb-6">
            <p className="text-sm text-[#64748B] mb-1 font-normal">Venue / Link</p>
             <div className="border-t border-[#E2E8F0] mt-1 mb-2"></div>
            <div className="flex items-center gap-2">
              <MapPin size={16} weight="fill" className="text-slate-500 flex-shrink-0" />
              {isVirtual && event.meeting_link ? (
                <a 
                  href={event.meeting_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base text-[#3167fe] font-normal hover:underline"
                >
                  {event.meeting_link}
                </a>
              ) : (
                <span className="text-base text-[#1E293B] font-normal">
                  {isVirtual ? (event.venue || 'No link provided') : (event.venue || 'No venue')}
                </span>
              )}
            </div>
          </div>

          {/* Description Section */}
          <div className="mb-4">
            <p className="text-sm text-[#64748B] mb-1 font-normal">Description</p>
            <div className="border-t border-slate-200 mt-1 mb-2"></div>
            <p className="text-base text-[#1E293B] leading-relaxed">
              {event.description || 'No description'}
            </p>
          </div>

          {/* Action buttons - for upcoming and active events */}
          {(eventCategory === 'upcoming' || eventCategory === 'active') && eventStatus !== 'attended' && (
            <div className="mb-4">
              {eventStatus === 'confirmed' ? (
                <button
                  onClick={handleDecline}
                  className="w-full px-4 py-2 rounded-[10px] text-base font-normal text-slate-700 bg-white border-2 border-slate-300 hover:bg-slate-50 focus:outline-none focus:border-slate-400 active:border-slate-400 transition-colors"
                >
                  Change to decline
                </button>
              ) : eventStatus === 'declined' ? (
                <button
                  onClick={handleConfirm}
                  className="w-full px-4 py-2 rounded-[10px] text-base font-normal text-white bg-[#3167fe] hover:bg-[#2557ed] focus:outline-none transition-colors"
                >
                  Change to confirm
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={handleConfirm}
                    className="flex-1 bg-[#3167fe] text-white px-4 py-2 rounded-[10px] text-base font-normal hover:bg-[#2557ed] focus:outline-none transition-colors"
                  >
                    Confirm attendance
                  </button>
                  <button
                    onClick={handleDecline}
                    className="px-4 py-2 rounded-[10px] text-base font-normal text-slate-700 bg-white border-2 border-slate-300 hover:bg-slate-50 focus:outline-none focus:border-slate-400 active:border-slate-400 transition-colors"
                  >
                    Decline
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Event ended text - only for past events */}
          {eventCategory === 'past' && (
            <p className="text-center text-xs text-slate-500 mt-6">
              Event ended at {event.end_time}
            </p>
          )}

          {/* Members Attendance Section */}
          <div className="mt-8 mb-6">
            <div className="bg-white rounded-[20px] border border-slate-200 p-6">
              <h3 className="text-xl font-semibold text-[#1E293B] mb-4">Members attendance</h3>

              {/* Two Dropdowns */}
              <div className="flex items-center gap-2 mb-4">
                {/* Department Filter */}
                <div className="relative">
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="appearance-none bg-white border border-slate-300 rounded-lg px-3 py-2 pr-8 text-sm text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All departments</option>
                    {subgroups.map(sg => (
                      <option key={sg.id} value={sg.id}>{sg.name}</option>
                    ))}
                  </select>
                  <CaretDown size={16} weight="regular" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-600" />
                </div>

                {/* Status Filter */}
                <div className="relative">
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="appearance-none bg-white border border-slate-300 rounded-lg px-3 py-2 pr-8 text-sm text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All</option>
                    {eventCategory === 'past' ? (
                      <>
                        <option value="attended">Attended</option>
                        <option value="missed">Missed</option>
                        <option value="declined">Declined</option>
                      </>
                    ) : (
                      <>
                        <option value="confirmed">Confirmed</option>
                        <option value="declined">Declined</option>
                        <option value="pending">Pending</option>
                      </>
                    )}
                  </select>
                  <CaretDown size={16} weight="regular" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-600" />
                </div>
              </div>

              {/* Stats Cards */}
              {!loadingMembers && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {eventCategory === 'past' ? (
                    <>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">Attended</p>
                        <p className="text-2xl font-semibold text-slate-900">{getStats().attended}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">Missed</p>
                        <p className="text-2xl font-semibold text-slate-900">{getStats().missed}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">Declined</p>
                        <p className="text-2xl font-semibold text-slate-900">{getStats().declined}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">Confirmed</p>
                        <p className="text-2xl font-semibold text-slate-900">{getStats().confirmed}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">Declined</p>
                        <p className="text-2xl font-semibold text-slate-900">{getStats().declined}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">Pending</p>
                        <p className="text-2xl font-semibold text-slate-900">{getStats().pending}</p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Total Members Count - Responds to department filter only */}
              <p className="text-sm font-medium text-[#475569] mb-2">
                Total members ({selectedDepartment === 'all' ? members.length : members.filter(m => m.subgroup_id === selectedDepartment).length})
                {selectedStatus !== 'all' && (
                  <span className="text-slate-400"> · Showing {getFilteredMembers().length}</span>
                )}
              </p>

              {/* Members List */}
              {loadingMembers ? (
                <div className="py-8 text-center">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-sm text-slate-500">Loading members...</p>
                </div>
              ) : getFilteredMembers().length === 0 ? (
                <div className="py-8 flex flex-col items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                    <User size={24} weight="light" className="text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500">No members found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {getFilteredMembers().map((member) => (
                    <div key={member.id} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {/* Avatar */}
                        {member.profile_photo_url ? (
                          <img
                            src={member.profile_photo_url}
                            alt={member.name}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className={`w-10 h-10 rounded-full ${getAvatarColor(member.name).bg} flex items-center justify-center flex-shrink-0`}>
                            <span className={`text-sm font-semibold ${getAvatarColor(member.name).text}`}>
                              {getInitials(member.name)}
                            </span>
                          </div>
                        )}
                        
                        {/* Name and Department */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{member.name}</p>
                          <p className="text-xs text-slate-500 truncate">{member.subgroup}</p>
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="flex-shrink-0 ml-2">
                        {getMemberStatusBadge(member.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}